import React, { useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { Modal, Button, Form, Row, Col, Spinner, Alert } from "react-bootstrap";
import useApi from "../../hooks/useApi";
import { useAuth } from "../../context/AuthContext";
import AuthModals from "../../component/AuthModals";
import Swal from "sweetalert2";
import { resolveAssetUrl } from "../../utils/assetUrl";


const ServiceDetail = () => {
  const { slug } = useParams();
  const { bearerAuth,publicAuth } = useApi();
  const { user: authUser } = useAuth();
  const razorpayKey = process.env.REACT_APP_RAZORPAY_KEY || "";

  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  // details (API #3)
  const [detailModal, setDetailModal] = useState(false);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailError, setDetailError] = useState("");
  const [detail, setDetail] = useState(null);

  // booking (POST v1/service/book)
  const [showBooking, setShowBooking] = useState(false);
  const [bookingLoading, setBookingLoading] = useState(false);
  const [bookingError, setBookingError] = useState("");
  const [bookingSuccess, setBookingSuccess] = useState("");
  const [contact, setContact] = useState({
    contact_name: "", contact_phone: "", contact_email: "",
    address_line1: "", address_line2: "", city: "", state: "", zipcode: "",
    scheduled_date: "", scheduled_time: "",
    price: "", discount: "", tax: "", notes: "",
  });
  
  const [pendingBooking, setPendingBooking] = useState(false);

  const [showLogin, setShowLogin] = useState(false);
  const [showRegister, setShowRegister] = useState(false);

  // avoid duplicate commits / strict mode races
  const fetchSeq = useRef(0);

  useEffect(() => {
    let active = true;
    const seq = ++fetchSeq.current;

    const fetchServices = async () => {
      const queryString = `services?vendor_name=${encodeURIComponent(slug)}`;
      try {
        setLoading(true);
        setError("");
        const res = await publicAuth(queryString);
        if (!active || seq !== fetchSeq.current) return;
        const list = res?.data?.data ?? res?.data ?? [];
        setServices(Array.isArray(list) ? list : []);
      } catch (err) {
        if (!active || seq !== fetchSeq.current) return;
        setError(err?.response?.data?.message || err?.message || "Failed to load services");
        setServices([]);
      } finally {
        if (active && seq === fetchSeq.current) setLoading(false);
      }
    };

    fetchServices();
    return () => { active = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slug]);

  const openDetails = async (serviceId) => {
    setDetail(null);
    setDetailError("");
    setDetailLoading(true);
    setDetailModal(true);
    try {
      const res = await publicAuth(`service?service_id=${serviceId}`);
      const payload = res?.data?.data ?? res?.data ?? null;
      const svc = payload?.service || payload;
      if (!svc) throw new Error("Service not found");
      setDetail(payload?.service ? payload : { service: svc });
    } catch (err) {
      setDetailError(err?.response?.data?.message || err?.message || "Failed to load details");
    } finally {
      setDetailLoading(false);
    }
  };

  const openBookingFor = (service) => {
    setDetail((prev) => prev || { service });
    setShowBooking(true);
  };

  useEffect(() => {
    if (!showBooking || !authUser) return;
    setContact((prev) => ({
      ...prev,
      contact_name: prev.contact_name || authUser.full_name || authUser.username || "",
      contact_phone: prev.contact_phone || String(authUser.contact || ""),
      contact_email: prev.contact_email || authUser.email || "",
      city: prev.city || authUser.city || "",
      zipcode: prev.zipcode || String(authUser.zipcode || ""),
    }));
  }, [showBooking, authUser]);

  const createPaymentKey = () =>
    `service_${Date.now()}_${Math.random().toString(36).slice(2)}`;

  const loadRazorpayScript = () =>
    new Promise((resolve, reject) => {
      if (window.Razorpay) {
        resolve(true);
        return;
      }
      const script = document.createElement("script");
      script.src = "https://checkout.razorpay.com/v1/checkout.js";
      script.onload = () => resolve(true);
      script.onerror = () => reject(new Error("Unable to load Razorpay checkout."));
      document.body.appendChild(script);
    });

  const servicePaymentRequest = (url, payload) => {
    if (authUser) return bearerAuth(url, "POST", payload);
    return publicAuth(url, "POST", payload);
  };

  const startServicePayment = async (payload) => {
    await loadRazorpayScript();
    const orderRes = await servicePaymentRequest("service/payments/razorpay/order", payload);
    const order = orderRes?.data?.data;
    if (!order?.order_id) {
      throw new Error(orderRes?.data?.message || "Unable to create payment order.");
    }

    const checkoutKey = order.key || razorpayKey;
    if (!checkoutKey) {
      throw new Error("Razorpay key is not configured.");
    }

    return new Promise((resolve, reject) => {
      const razorpay = new window.Razorpay({
        key: checkoutKey,
        amount: order.amount_paisa,
        currency: order.currency || "INR",
        name: "Asysta",
        description: "Service Booking Payment",
        order_id: order.order_id,
        prefill: {
          name: payload.contact_name || authUser?.full_name || "",
          email: payload.contact_email || authUser?.email || "",
          contact: payload.contact_phone || authUser?.contact || "",
        },
        handler: async (response) => {
          try {
            const verifyRes = await servicePaymentRequest("service/payments/razorpay/verify", {
              vendor_id: payload.vendor_id,
              service_id: payload.service_id,
              payment_key: payload.payment_key,
              contact_email: payload.contact_email,
              contact_phone: payload.contact_phone,
              razorpay_order_id: response.razorpay_order_id,
              razorpay_payment_id: response.razorpay_payment_id,
              razorpay_signature: response.razorpay_signature,
            });
            const verified = verifyRes?.data?.data;
            if (!verifyRes?.data?.status || !verified?.payment_token) {
              throw new Error(verifyRes?.data?.message || "Payment verification failed.");
            }
            resolve(verified);
          } catch (err) {
            reject(new Error(err?.response?.data?.message || err?.message || "Payment verification failed."));
          }
        },
        modal: {
          ondismiss: () => reject(new Error("Payment was cancelled.")),
        },
        theme: { color: "#284eb8" },
      });

      razorpay.on("payment.failed", (response) => {
        reject(new Error(response?.error?.description || "Payment failed."));
      });

      razorpay.open();
    });
  };

  useEffect(() => {
    if (authUser && pendingBooking) {
      setShowLogin(false);
      setShowRegister(false);
      setPendingBooking(false);
      setShowBooking(true);
    }
  }, [authUser, pendingBooking]);

  const handleBook = async (e) => {
    e.preventDefault();
    const svcId = detail?.service?.id;
    if (!svcId) return;

    setBookingError("");
    setBookingSuccess("");

    try {
      setBookingLoading(true);
      if (!contact.contact_name || !contact.contact_phone || !contact.contact_email) {
        throw new Error("Please enter name, phone and email before payment.");
      }
      const vendorId = detail?.service?.vendor_id;
      if (!vendorId) {
        throw new Error("Vendor details are missing for this service.");
      }
      const payload = {
        service_id: [svcId],
        payment_key: createPaymentKey(),
        scheduled_date: contact.scheduled_date || null,
        scheduled_time: contact.scheduled_time || null,
        contact_name: contact.contact_name || null,
        contact_phone: contact.contact_phone || null,
        contact_email: contact.contact_email || null,
        address_line1: contact.address_line1 || null,
        address_line2: contact.address_line2 || null,
        vendor_id: vendorId,
        city: contact.city || null,
        state: contact.state || null,
        zipcode: contact.zipcode || null,
        // pricing computed server-side
        notes: contact.notes || null,
      };

      const paymentData = await startServicePayment(payload);
      payload.payment_token = paymentData.payment_token;

      const res = authUser
        ? await bearerAuth(`service/book`, "POST", payload)
        : await publicAuth(`service/book`, "POST", payload);
      const ok = res?.data?.status === true;
      if (!ok) throw new Error(res?.data?.message || "Booking failed");
Swal.fire({
  icon: "success",
  title: "Booking confirmed",
  text: res?.data?.message || "Service booked successfully",
  timer: 2200,
  showConfirmButton: false,
  timerProgressBar: true,
  position: "top-end",
  toast: true,
});
      setBookingSuccess(res?.data?.message || "Service booked successfully");
      setShowBooking(false);
    } catch (err) {
      setBookingError(err?.response?.data?.message || err?.message || "Booking failed");
    } finally {
      setBookingLoading(false);
    }
  };

  return (
    <>
      {loading && (
        <div className="text-center py-3">
          <Spinner animation="border" /> Loading...
        </div>
      )}
      {error && !loading && (
        <Alert variant="danger" className="text-center">
          {error}
        </Alert>
      )}

      {!loading && !error && (
        <section className="service-details pt-100 pb-70">
          <div className="auto-container">
             <h2>View all services of {decodeURIComponent(slug)}</h2>
            <div className="row clearfix">
              {services.length === 0 && (
                <div className="col-12 text-center">
                  <p>No data found</p>
                </div>
              )}
              {services.map((s) => (
                <div key={s.id} className="col-lg-4 col-md-6 col-sm-12 service-block">
                  <div className="service-block-one">
                    <div className="feature-item p-3 border rounded h-100 d-flex flex-column">
                      <div className="image-box mb-3">
                        <img
                          src={
                            resolveAssetUrl(
                              s.image_url || s.image,
                              `${process.env.PUBLIC_URL}/assets/images/service/service-1.jpg`
                            )
                          }
                          alt={s.service_name || "Service"}
                          className="img-fluid"
                          style={{ height: 200, width: "100%", objectFit: "cover", borderRadius: 8 }}
                          onError={(e) => (e.currentTarget.style.display = "none")}
                        />
                      </div>
                      <h5 className="mb-1">{s.service_name || "Service"}</h5>
                      <div className="text-muted mb-2">{s.services_category || ""}</div>
                      <div className="mt-auto d-flex gap-2">
                        <Button variant="outline-primary" size="sm" onClick={() => openDetails(s.id)}>
                          View Details
                        </Button>
                        <Button variant="primary" size="sm" onClick={() => openBookingFor(s)}>
                          Book Now
                        </Button>
                      </div>
                    </div>
                  </div>
                </div>
              ))}

            </div>
          </div>
        </section>
      )}

      {/* Details Modal */}
      <Modal show={detailModal} onHide={() => setDetailModal(false)} size="lg" centered>
        <Modal.Header closeButton>
          <Modal.Title>Service Details</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {detailLoading && <div className="text-center py-2"><Spinner animation="border" /> Loading...</div>}
          {detailError && <Alert variant="danger">{detailError}</Alert>}
          {!detailLoading && !detailError && detail?.service && (
            <>
              <h4 className="mb-2">{detail.service.service_name || "Service"}</h4>
              {detail.service.price != null && (<p><strong>Price:</strong> {detail.service.price}</p>)}
              {detail.service.duration && (<p><strong>Duration:</strong> {detail.service.duration}</p>)}
              {detail.service.description && <p>{detail.service.description}</p>}
              <img
                src={resolveAssetUrl(
                  detail.service.image_url || detail.service.image,
                  `${process.env.PUBLIC_URL}/assets/images/service/service-1.jpg`
                )}
                alt={detail.service.service_name || "Service"}
                style={{ maxWidth: "100%", borderRadius: 8 }}
                onError={(e) => (e.currentTarget.style.display = "none")}
              />
            </>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setDetailModal(false)}>Close</Button>
          <Button variant="primary" onClick={() => detail?.service && openBookingFor(detail.service)} disabled={!detail?.service}>
            Book Now
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Auth Modals */}
      <AuthModals
        showLogin={showLogin}
        showRegister={showRegister}
        onCloseLogin={() => { setShowLogin(false); setPendingBooking(false); }}
        onCloseRegister={() => { setShowRegister(false); setPendingBooking(false); }}
        openRegisterFromLogin={() => { setShowLogin(false); setShowRegister(true); }}
        openLoginFromRegister={() => { setShowRegister(false); setShowLogin(true); }}
        navigation={false}
      />

      {/* Booking Modal */}
      <Modal show={showBooking} onHide={() => setShowBooking(false)} size="lg" centered backdrop="static">
        <Modal.Header closeButton>
          <Modal.Title>Book {detail?.service?.service_name || "Service"}</Modal.Title>
        </Modal.Header>
        <Form onSubmit={handleBook}>
          <Modal.Body>
            {bookingError && <Alert variant="danger">{bookingError}</Alert>}
            {bookingSuccess && <Alert variant="success">{bookingSuccess}</Alert>}

            <Row className="mb-3">
              <Form.Group as={Col} md="4">
                <Form.Label>Scheduled Date</Form.Label>
                <Form.Control type="date" value={contact.scheduled_date} onChange={(e) => setContact({ ...contact, scheduled_date: e.target.value })} />
              </Form.Group>
              <Form.Group as={Col} md="4">
                <Form.Label>Scheduled Time</Form.Label>
                <Form.Control type="time" value={contact.scheduled_time} onChange={(e) => setContact({ ...contact, scheduled_time: e.target.value })} />
              </Form.Group>
            </Row>

            <Row className="mb-3">
              <Form.Group as={Col} md="4">
                <Form.Label>Contact Name</Form.Label>
                <Form.Control type="text" value={contact.contact_name} onChange={(e) => setContact({ ...contact, contact_name: e.target.value })} />
              </Form.Group>
              <Form.Group as={Col} md="4">
                <Form.Label>Phone</Form.Label>
                <Form.Control type="text" value={contact.contact_phone} onChange={(e) => setContact({ ...contact, contact_phone: e.target.value })} />
              </Form.Group>
              <Form.Group as={Col} md="4">
                <Form.Label>Email</Form.Label>
                <Form.Control type="email" value={contact.contact_email} onChange={(e) => setContact({ ...contact, contact_email: e.target.value })} />
              </Form.Group>
            </Row>

            <Row className="mb-3">
              <Form.Group as={Col} md="6">
                <Form.Label>Address Line 1</Form.Label>
                <Form.Control type="text" value={contact.address_line1} onChange={(e) => setContact({ ...contact, address_line1: e.target.value })} />
              </Form.Group>
              <Form.Group as={Col} md="6">
                <Form.Label>Address Line 2</Form.Label>
                <Form.Control type="text" value={contact.address_line2} onChange={(e) => setContact({ ...contact, address_line2: e.target.value })} />
              </Form.Group>
            </Row>

            <Row className="mb-3">
              <Form.Group as={Col} md="4">
                <Form.Label>City</Form.Label>
                <Form.Control type="text" value={contact.city} onChange={(e) => setContact({ ...contact, city: e.target.value })} />
              </Form.Group>
              <Form.Group as={Col} md="4">
                <Form.Label>State</Form.Label>
                <Form.Control type="text" value={contact.state} onChange={(e) => setContact({ ...contact, state: e.target.value })} />
              </Form.Group>
              <Form.Group as={Col} md="4">
                <Form.Label>Zipcode</Form.Label>
                <Form.Control type="text" value={contact.zipcode} onChange={(e) => setContact({ ...contact, zipcode: e.target.value })} />
              </Form.Group>
            </Row>

            {/* <Row className="mb-3">
              <Form.Group as={Col} md="6">
                <Form.Label>Payment Method</Form.Label>
                <Form.Control type="text" value={contact.payment_method} onChange={(e) => setContact({ ...contact, payment_method: e.target.value })} />
              </Form.Group>
              <Form.Group as={Col} md="6">
                <Form.Label>Payment Ref</Form.Label>
                <Form.Control type="text" value={contact.payment_ref} onChange={(e) => setContact({ ...contact, payment_ref: e.target.value })} />
              </Form.Group>
            </Row> */}

            <Row>
              <Form.Group as={Col}>
                <Form.Label>Notes</Form.Label>
                <Form.Control as="textarea" rows={2} value={contact.notes} onChange={(e) => setContact({ ...contact, notes: e.target.value })} />
              </Form.Group>
            </Row>
          </Modal.Body>

          <Modal.Footer>
            <Button variant="secondary" onClick={() => setShowBooking(false)}>Cancel</Button>
            <Button type="submit" variant="primary" disabled={bookingLoading}>
              {bookingLoading ? (<><Spinner animation="border" size="sm" className="me-2" /> Booking…</>) : ("Confirm Booking")}
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>
    </>
  );
};

export default ServiceDetail;
